// src/screens/DirectoryGrowth.js — what Sana did to the directory overnight,
// what she will try next, and the rows waiting for a human (22 Sep 2026).
//
// Read-only except the candidate queue, where three verbs mirror the app's
// trust states: Verify (a human read the source or called), Closed (hidden
// from search), Wrong (Sana re-checks tonight). Everything comes from
// /api/directory-growth on the service key; nothing here is computed locally
// from a guess.

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardTitle, Grid, MetricCard, Pill, Btn, Insight, EmptyState, Input } from '../components/UI';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';

function when(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return isNaN(d) ? '—' : d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
function usd(n) { return n == null ? '—' : '$' + Number(n).toFixed(2); }
function host(u) { try { return new URL(u).hostname.replace(/^www\./, ''); } catch (_e) { return u || ''; } }

const REJECT_LABEL = {
  no_coordinates: 'no pin', no_phone_or_website: 'no phone/site', no_source: 'no source page',
  no_address: 'no address', no_name: 'no name', insert_error: 'insert failed',
};

export default function DirectoryGrowth() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null);
  const [city, setCity] = useState('');
  const [openRun, setOpenRun] = useState(null);

  const authHeaders = useCallback(async () => {
    const { data: s } = await supabase.auth.getSession();
    const token = s && s.session && s.session.access_token;
    return token ? { Authorization: 'Bearer ' + token } : {};
  }, []);

  const load = useCallback(async (cityFilter) => {
    setErr('');
    try {
      const q = cityFilter ? '?city=' + encodeURIComponent(cityFilter) : '';
      const r = await fetch('/api/directory-growth' + q, { headers: await authHeaders() });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'load failed');
      setData(j);
    } catch (e) { setErr(e.message); setData({ runs: [], queue: [], misses: [], trust: [], candidates: [], embassies: [], embassiesVerified: 0 }); }
  }, [authHeaders]);

  useEffect(() => { load(''); }, [load]);

  async function act(id, action) {
    if (busy) return;
    setBusy(id + action);
    setErr('');
    try {
      const r = await fetch('/api/directory-growth', { method: 'POST', headers: { 'Content-Type': 'application/json', ...(await authHeaders()) }, body: JSON.stringify({ id, action }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'action failed');
      setData((d) => d ? { ...d, candidates: d.candidates.filter((c) => c.id !== id) } : d);
    } catch (e) { setErr(e.message); } finally { setBusy(null); }
  }

  // Embassy rows (22 Sep 2026): same three verbs, their own table. A row
  // verified here is what the app labels "checked against the ministry page".
  async function actEmbassy(id, action) {
    if (busy) return;
    setBusy('emb' + id + action);
    setErr('');
    try {
      const r = await fetch('/api/directory-growth', { method: 'POST', headers: { 'Content-Type': 'application/json', ...(await authHeaders()) }, body: JSON.stringify({ kind: 'embassy', id, action }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'action failed');
      setData((d) => d ? { ...d, embassies: (d.embassies || []).filter((c) => c.id !== id), embassiesVerified: (d.embassiesVerified || 0) + (action === 'verify' ? 1 : 0) } : d);
    } catch (e) { setErr(e.message); } finally { setBusy(null); }
  }

  if (!data) return <div style={{ fontFamily: F, color: '#7b8b94', padding: 24 }}>Loading…</div>;

  const trust = Object.fromEntries((data.trust || []).map((t) => [t.trust, Number(t.n)]));
  const last = data.runs.find((r) => r.kind === 'nightly') || data.runs[0] || null;
  const spend30 = data.runs.reduce((s, r) => s + (Number(r.cost_usd) || 0), 0);
  const inserted30 = data.runs.reduce((s, r) => s + (Number(r.inserted) || 0), 0);

  return (
    <div style={{ fontFamily: F }}>
      {err ? <Insight type="warn">{err}</Insight> : null}

      <Grid cols={4}>
        <MetricCard label="Verified" value={(trust.verified || 0).toLocaleString()} sub="trust = verified, within 12 months" accent="#17a86a" />
        <MetricCard label="Found by Sana, unchecked" value={(trust.candidate || 0).toLocaleString()} sub="candidates awaiting a call, visit or re-check" accent="#d97706" />
        <MetricCard label="Needs a re-check" value={(trust.check_needed || 0).toLocaleString()} sub="verified once, over a year ago, or reported" accent="#e53935" />
        <MetricCard label="Never checked" value={(trust.unverified || 0).toLocaleString()} sub="imported rows with no verification (estimate)" />
      </Grid>

      <Grid cols={3}>
        <MetricCard label="Last nightly run" value={last ? `${last.inserted || 0} added` : '—'} sub={last ? `${when(last.started_at)} · ${last.asked || 0} Sana calls · ${last.rejected || 0} rejected · ${last.rechecked || 0} re-checked` : 'No run yet — first one is 03:20 UTC'} />
        <MetricCard label="Added, last 30 runs" value={inserted30.toLocaleString()} sub="candidate rows inserted by Sana" />
        <MetricCard label="Spend, last 30 runs" value={usd(spend30)} sub="Anthropic web-search calls, at $0.085 each (estimate)" />
      </Grid>

      {/* ── Candidate review ─────────────────────────────────────────── */}
      <Card>
        <CardTitle action={<Input value={city} onChange={(e) => { setCity(e.target.value); }} placeholder="Filter by city…" style={{ width: 180 }} />}>
          Found by Sana — waiting for a human ({data.candidates.length})
        </CardTitle>
        <div style={{ marginBottom: 8 }}><Btn small variant="outline" onClick={() => load(city.trim())}>Apply filter</Btn></div>
        {data.candidates.length === 0 ? <EmptyState title="Nothing waiting" body="Sana has no unchecked rows in this view." icon="✓" /> : null}
        {data.candidates.map((c) => (
          <div key={c.id} style={{ borderTop: '1px solid #e2ebf0', padding: '12px 0', display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ fontWeight: 600, color: '#16303f' }}>{c.name}{c.local_name && c.local_name !== c.name ? <span style={{ color: '#7b8b94', fontWeight: 400 }}> · {c.local_name}</span> : null}</div>
              <div style={{ fontSize: 12.5, color: '#4a6878', marginTop: 2 }}>{[c.canonical_type, c.city, c.country].filter(Boolean).join(' · ')} · found {when(c.found_at)}</div>
              <div style={{ fontSize: 12.5, color: '#4a6878', marginTop: 2 }}>{c.address}</div>
              <div style={{ fontSize: 12.5, marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {c.phone ? <span>{c.phone}</span> : <Pill type="amber">no phone</Pill>}
                {c.website ? <a href={c.website} target="_blank" rel="noreferrer" style={{ color: '#2563eb' }}>{host(c.website)}</a> : <Pill type="amber">no website</Pill>}
                {c.source_url ? <a href={c.source_url} target="_blank" rel="noreferrer" style={{ color: '#2563eb' }}>source: {host(c.source_url)}</a> : null}
                {c.latitude != null ? <a href={`https://www.openstreetmap.org/?mlat=${c.latitude}&mlon=${c.longitude}#map=17/${c.latitude}/${c.longitude}`} target="_blank" rel="noreferrer" style={{ color: '#2563eb' }}>map</a> : null}
                {c.languages ? <span style={{ color: '#7b8b94' }}>speaks {c.languages}</span> : null}
              </div>
              {c.sana_note ? <div style={{ fontSize: 12.5, color: '#7b8b94', marginTop: 4, fontStyle: 'italic' }}>{c.sana_note}</div> : null}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <Btn small variant="green" onClick={() => act(c.id, 'verify')}>{busy === c.id + 'verify' ? '…' : 'Verify'}</Btn>
              <Btn small variant="outline" onClick={() => act(c.id, 'wrong')}>{busy === c.id + 'wrong' ? '…' : 'Wrong — re-check'}</Btn>
              <Btn small variant="danger" onClick={() => act(c.id, 'closed')}>{busy === c.id + 'closed' ? '…' : 'Closed'}</Btn>
            </div>
          </div>
        ))}
      </Card>

      {/* ── Embassies Sana found (22 Sep 2026) ───────────────────── */}
      <Card>
        <CardTitle>Embassies found by Sana — waiting for a human ({(data.embassies || []).length}) · {data.embassiesVerified || 0} verified</CardTitle>
        <Insight type="info">Sana reads each foreign ministry's own list of missions abroad. Verify means you opened the source link and the address and phone match; the app then says "checked against the ministry page". Closed hides the row from the app.</Insight>
        {(data.embassies || []).length === 0 ? <EmptyState title="Nothing waiting" body="No unchecked missions. Sana adds more each night for the passports members hold." icon="✓" /> : null}
        {(data.embassies || []).map((c) => (
          <div key={c.id} style={{ borderTop: '1px solid #e2ebf0', padding: '12px 0', display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ fontWeight: 600, color: '#16303f' }}>{c.name} <Pill type={c.trust === 'check_needed' ? 'amber' : 'blue'}>{c.trust}</Pill></div>
              <div style={{ fontSize: 12.5, color: '#4a6878', marginTop: 2 }}>{c.nationality} ({c.nationality_iso3}) in {c.host_country} · {c.mission_type.replace(/_/g, ' ')} · {c.city} · found {when(c.found_at)} by {c.found_by}</div>
              <div style={{ fontSize: 12.5, color: '#4a6878', marginTop: 2 }}>{c.address || <Pill type="amber">no address</Pill>}</div>
              <div style={{ fontSize: 12.5, marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {c.phone ? <span>{c.phone}</span> : <Pill type="amber">no phone</Pill>}
                {c.emergency_phone ? <span style={{ color: '#b91c1c' }}>out of hours {c.emergency_phone}</span> : null}
                {c.email ? <span>{c.email}</span> : null}
                {c.website ? <a href={c.website} target="_blank" rel="noreferrer" style={{ color: '#2563eb' }}>{host(c.website)}</a> : null}
                {c.source_url ? <a href={c.source_url} target="_blank" rel="noreferrer" style={{ color: '#2563eb' }}>source: {c.source_name || host(c.source_url)}</a> : <Pill type="red">no source</Pill>}
              </div>
              {c.services_note ? <div style={{ fontSize: 12.5, color: '#7b8b94', marginTop: 4, fontStyle: 'italic' }}>{c.services_note}</div> : null}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <Btn small variant="green" onClick={() => actEmbassy(c.id, 'verify')}>{busy === 'emb' + c.id + 'verify' ? '…' : 'Verify'}</Btn>
              <Btn small variant="outline" onClick={() => actEmbassy(c.id, 'wrong')}>{busy === 'emb' + c.id + 'wrong' ? '…' : 'Wrong — re-check'}</Btn>
              <Btn small variant="danger" onClick={() => actEmbassy(c.id, 'closed')}>{busy === 'emb' + c.id + 'closed' ? '…' : 'Closed'}</Btn>
            </div>
          </div>
        ))}
      </Card>

      <Grid cols={2}>
        {/* ── Gap queue ────────────────────────────────────────────── */}
        <Card>
          <CardTitle>Tonight's queue — cells Sana will try first</CardTitle>
          <div style={{ fontSize: 12, color: '#7b8b94', marginBottom: 8 }}>Trips arriving within 14 days score ×10, searches that found nothing ×3, then the thinnest cells. Read live from sn_gap_queue.</div>
          {data.queue.length === 0 ? <EmptyState title="Queue is empty" body="No trips arriving, no misses, no thin cells scored." icon="·" /> : null}
          {data.queue.map((q, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e2ebf0', padding: '7px 0', fontSize: 13 }}>
              <span>{q.city}, {q.country} · <b>{q.venue_type}</b></span>
              <span style={{ color: '#7b8b94' }}>{q.verified_now != null ? `${q.verified_now} verified` : ''}{q.trips_arriving ? ` · ${q.trips_arriving} trips` : ''}{q.misses_30d ? ` · ${q.misses_30d} misses` : ''}{q.score != null ? ` · score ${q.score}` : ''}</span>
            </div>
          ))}
        </Card>

        {/* ── Zero-result searches ─────────────────────────────────── */}
        <Card>
          <CardTitle>Searches that found nothing — last 7 days</CardTitle>
          <div style={{ fontSize: 12, color: '#7b8b94', marginBottom: 8 }}>From search_events. Each is a demand signal the queue reads.</div>
          {data.misses.length === 0 ? <EmptyState title="No empty searches" body="Every search this week returned something." icon="✓" /> : null}
          {data.misses.map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e2ebf0', padding: '7px 0', fontSize: 13 }}>
              <span>{m.city}{m.country ? `, ${m.country}` : ''} · <b>{m.venue_type || 'any'}</b></span>
              <span style={{ color: '#7b8b94' }}>{m.searches}× · {when(m.last_seen)}</span>
            </div>
          ))}
        </Card>
      </Grid>

      {/* ── Runs ─────────────────────────────────────────────────────── */}
      <Card>
        <CardTitle>Runs</CardTitle>
        {data.runs.length === 0 ? <EmptyState title="No runs yet" body="The nightly job runs at 03:20 UTC; on-demand fills appear here as they happen." icon="·" /> : null}
        {data.runs.map((r) => {
          const cells = r.cells && Array.isArray(r.cells.cells) ? r.cells.cells : (r.cells && r.cells.city ? [r.cells] : []);
          const rej = (r.cells && r.cells.rejections) || {};
          const open = openRun === r.id;
          return (
            <div key={r.id} style={{ borderTop: '1px solid #e2ebf0', padding: '9px 0', fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', cursor: 'pointer' }} onClick={() => setOpenRun(open ? null : r.id)}>
                <span><Pill type={r.kind === 'nightly' ? 'blue' : 'green'}>{r.kind}</Pill> <span style={{ marginLeft: 8 }}>{when(r.started_at)}</span>{r.note ? <span style={{ color: '#e53935', marginLeft: 8 }}>{r.note}</span> : null}</span>
                <span style={{ color: '#4a6878' }}>{r.asked || 0} calls · <b>{r.inserted || 0} added</b> · {r.rejected || 0} rejected · {r.duplicates || 0} dup · {r.rechecked || 0} re-checked ({r.recheck_matched || 0} matched) · {r.decayed || 0} decayed · {r.folded || 0} folded · {usd(r.cost_usd)}</span>
              </div>
              {open ? (
                <div style={{ marginTop: 6, paddingLeft: 8, color: '#4a6878' }}>
                  {Object.keys(rej).length ? <div style={{ marginBottom: 4 }}>Rejected: {Object.entries(rej).map(([k, v]) => `${REJECT_LABEL[k] || k} ${v}`).join(' · ')}</div> : null}
                  {cells.map((c, i) => (
                    <div key={i}>{c.city}, {c.country} · {c.type || c.venue_type}{c.inserted != null ? ` — ${c.inserted} added, ${c.rejected || 0} rejected, ${c.duplicates || 0} dup` : ''}{c.rejections ? ' · ' + Object.entries(c.rejections).map(([k, v]) => `${REJECT_LABEL[k] || k} ${v}`).join(', ') : ''}{c.error ? ` · ${c.error}` : ''}</div>
                  ))}
                  {!cells.length && !Object.keys(rej).length ? <div>No cell detail recorded.</div> : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </Card>
    </div>
  );
}
